/** @file slotMethods: the slots a puppet needs, a slot's mask PNG, and tiles painted and read as text. */
import type { MaskReply, SlotPaint, SlotPaintReply, SlotPixel, SlotsReply, SlotText } from '../types/restAdded';
import type { ClientOptions } from './clientOptions';
import { fillPath } from './fillPath';
import { request } from './request';

/** The slot members of the client. */
export type SlotMethods = {
  /** `GET /api/v1/puppets/{id}/slots`; `drawable` keeps one drawable's slots, `empty` only empty (true) or filled ones. */
  slots: (puppetId: string, q?: { drawable?: string; empty?: boolean }) => Promise<SlotsReply>;
  /** `GET /api/v1/puppets/{id}/slots/{key}/mask`; `key` is `<drawable>/<entry>/<tile>`, sent as one encoded segment. */
  slotMask: (puppetId: string, key: string, style?: string) => Promise<MaskReply>;
  /** `POST /api/v1/puppets/{id}/paint`: up to 32 tiles as text (palette + rows), stored and filled in one batch. */
  slotPaint: (puppetId: string, paints: SlotPaint[]) => Promise<SlotPaintReply>;
  /** `GET /api/v1/puppets/{id}/slots/{key}/text`: the slot's image as text (palette from the pixels, rows). */
  slotRead: (puppetId: string, key: string) => Promise<SlotText>;
  /** `POST /api/v1/puppets/{id}/paint-pixels`: single pixels set on the slot's current image, then filled. */
  slotPaintPixels: (puppetId: string, slot: string, pixels: SlotPixel[]) => Promise<SlotPaintReply>;
};

const SLOTS = '/api/v1/puppets/{id}/slots';
const PUPPET = '/api/v1/puppets/{id}';

/** Slot calls over `opts`. */
export function slotMethods(opts: ClientOptions): SlotMethods {
  return {
    slots: async (id, q = {}) => (await request<SlotsReply>(opts, {
      method: 'GET', path: fillPath(SLOTS, { id }), query: { drawable: q.drawable, empty: q.empty === undefined ? undefined : String(q.empty) }, accept: 'json',
    })).value,
    slotMask: async (id, key, style) => (await request<MaskReply>(opts, {
      method: 'GET', path: fillPath(`${SLOTS}/{key}/mask`, { id, key }), query: { style }, accept: 'json',
    })).value,
    slotPaint: async (id, paints) => (await request<SlotPaintReply>(opts, {
      method: 'POST', path: fillPath(`${PUPPET}/paint`, { id }), body: { paints }, accept: 'json',
    })).value,
    slotRead: async (id, key) => (await request<SlotText>(opts, {
      method: 'GET', path: fillPath(`${SLOTS}/{key}/text`, { id, key }), accept: 'json',
    })).value,
    slotPaintPixels: async (id, slot, pixels) => (await request<SlotPaintReply>(opts, {
      method: 'POST', path: fillPath(`${PUPPET}/paint-pixels`, { id }), body: { slot, pixels }, accept: 'json',
    })).value,
  };
}
